import { LitElement, html } from '~/lib/bn-lit-element';
import React from 'react';
import ReactDOM from 'react-dom';
import { LocalizationProvider, AdapterLuxon, DatePicker, DateTimePicker, useMediaQuery, useTheme } from '~/lib/bn-material-ui';
import { DateTime } from 'luxon';
import './bn-icon';
import { sharedStyles } from '../styles/shared-styles';

class BnDatePicker extends LitElement {
  static get properties() {
    return {
      /* ISO string value */
      value: { type: String },
      disabled: { type: Boolean },
      min: { type: String },
      max: { type: String },
      noIcon: { type: Boolean, attribute: 'no-icon' },
      includeTime: { type: Boolean, attribute: 'include-time' },
    };
  }

  static get styles() {
    return [sharedStyles];
  }

  constructor() {
    super();
    this._handleChange = this._handleChange.bind(this);
    this._openPicker = this._openPicker.bind(this);
    this._open = false;
    this._anchorEl = null;
  }

  firstUpdated() {
    this._renderReact();
  }

  updated() {
    this._renderReact();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    const container = this.shadowRoot.getElementById('react-root');
    if (container) {
      ReactDOM.unmountComponentAtNode(container);
    }
  }

  _handleChange(date) {
    // luxon DateTime or null
    const iso = date ? (this.includeTime ? date.toUTC().toISO() : date.startOf('day').toUTC().toISO()) : '';
    if (iso !== this.value) {
      this.value = iso;
      this.dispatchEvent(new CustomEvent('change', { detail: { value: iso }, bubbles: true, composed: true }));
    }
    this.requestUpdate();
  }

  _openPicker(e) {
    const target = e.currentTarget;
    this._anchorEl = target;

    this._open = true;
    this._renderReact();
  }

  _renderReact() {
    const container = this.shadowRoot.getElementById('react-root');
    if (!container) {return;}

    const { value, disabled, min, max, includeTime } = this;
    const luxonValue = value ? DateTime.fromISO(value) : null;
    const minDate = min ? DateTime.fromISO(min) : undefined;
    const maxDate = max ? DateTime.fromISO(max) : undefined;

    const onClose = () => {
      this._open = false;
      this._renderReact();
    };

    const ResponsiveDatePicker = () => {
      const theme = useTheme();
      const isMobile = useMediaQuery(theme.breakpoints.down('md'));

      const buttonRect = this._anchorEl?.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = buttonRect ? viewportHeight - buttonRect.bottom : 300;
      const spaceAbove = buttonRect ? buttonRect.top : 300;
      const shouldOpenUpward = spaceAbove > spaceBelow && spaceBelow < 350;

      const pickerProps = {
        label: this.label || 'Select Date',
        value: luxonValue,
        onChange: this._handleChange,
        disabled,
        open: this._open,
        onClose,
        onOpen: () => {},
        ...(includeTime ? { minDateTime: minDate, maxDateTime: maxDate } : { minDate, maxDate }),
      };

      const slotProps = {
        // Hides the default text field, as we use our own button.
        textField: {
          style: { display: 'none' },
        },
        actionBar: {
          actions: isMobile ? ['clear', 'accept'] : [],
        },

        // --- Slot props specific to the mobile dialog view ---
        dialog: {
          sx: { zIndex: 11000 },
        },
        mobilePaper: {
          sx: {
            '& .MuiDayCalendar-weekDayLabel': { fontSize: '0.875rem' },
            '& .MuiPickersDay-root': { fontSize: '0.875rem' },
          },
        },

        // --- Slot props specific to the desktop popover view ---
        popper: {
          anchorEl: this._anchorEl,
          placement: shouldOpenUpward ? 'top-start' : 'bottom-start',
          modifiers: [
            {
              name: 'flip',
              options: {
                allowedAutoPlacements: shouldOpenUpward ? ['top-start', 'bottom-start'] : ['bottom-start', 'top-start'],
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: { boundary: 'viewport', padding: 8 },
            },
          ],
          sx: { zIndex: 11000 },
        },
        desktopPaper: {
          sx: {
            '& .MuiDayCalendar-weekDayLabel': { fontSize: '0.875rem' },
            '& .MuiPickersDay-root': { fontSize: '0.875rem' },
          },
        },
      };

      const PickerComponent = includeTime ? DateTimePicker : DatePicker;

      return React.createElement(PickerComponent, {
        ...pickerProps,
        slotProps,
      });
    };

    ReactDOM.render(
      React.createElement(LocalizationProvider, { dateAdapter: AdapterLuxon },
        React.createElement(ResponsiveDatePicker),
      ),
      container,
    );
  }

  _formatDisplay(value) {
    const dt = value ? DateTime.fromISO(value) : null;
    if (!dt) {return 'Select Date';}
    return this.includeTime ? dt.toFormat('MMM d, h:mm a') : dt.toFormat('MMM d');
  }

  render() {
    return html`
      <button 
        class="button button--text" 
        @click=${this._openPicker} 
        ?disabled=${this.disabled} 
        aria-label=${this.label || 'Open date picker'}
        style="
          padding: 8px 12px;
          min-height: 44px;
          display: flex;
          align-items: center;
          gap: 4px;
          touch-action: manipulation;
        "
      >
        ${this.noIcon ? '' : html`<bn-icon name="far fa-calendar-alt" style="margin-right: 4px;"></bn-icon>`}
        <span style="flex: 1; text-align: left;">${this._formatDisplay(this.value)}</span>
      </button>
      <div id="react-root" style="display: none;"></div>
    `;
  }
}

customElements.define('bn-date-picker', BnDatePicker);
